import React, { useCallback, useEffect, useRef, useState } from 'react';

import ConfirmModal from '../Common/Modal/ConfirmModal';
import { PaperAirplaneIcon, PlusSmallIcon } from '../Icon';
import ChatInputComponent from './ChatInputComponent';
import { colors } from '@/const/colors';
import { THEUNBAN } from '@/const/referralCode';
import usePartnerSession from '@/hooks/usePartnerSession';
import { base64ToBytes } from '@/utils/common';
import * as Sentry from '@sentry/react';

type ChatRoomDataProps = {
  handleInputHeightChange: (Height: number) => void;
  chatInput: string;
  updateChatInput: (data: string) => void;
  sendChat: () => void;
  imageList: File[];
  updateImageList: (data: File[]) => void;
};

const ChatRoomInput = ({
  handleInputHeightChange,
  chatInput,
  updateChatInput,
  sendChat,
  imageList,
  updateImageList,
}: ChatRoomDataProps) => {
  const isTheunban = usePartnerSession() === THEUNBAN;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>();

  const [isShowModal, setIsShowModal] = useState(false);

  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    title: '',
    content: '',
    leftButtonLabel: '',
    rightButtonLabel: '',
    onLeftButtonClick: () => {},
    onRightButtonClick: () => {},
  });

  const onChangeChat = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateChatInput(e.target.value);
  }, []);

  const handleClickPlusButton = () => {
    if (isTheunban) {
      if (window.FlutterChannel) {
        window.FlutterChannel.postMessage('runImagePicker');
      }
    }

    if (fileInputRef.current) {
      (fileInputRef.current as HTMLInputElement).click();
    }
  };

  const handleSendButtonClick = async () => {
    if (sending || !chatInput.trim()) {
      return;
    }
    setSending(true);
    try {
      sendChat();
    } catch (error) {
      console.error('Error sending chat:', error);
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const selectedFiles = e.target.files;
    const filesArray = Array.from(selectedFiles);

    const allowedExtensions = ['jpg', 'jpeg', 'png'];

    const hasInvalidFile = filesArray.some((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return !ext || !allowedExtensions.includes(ext);
    });

    if (hasInvalidFile) {
      setIsShowModal(true);
      setModalConfig({
        title: '',
        content: (
          <span>
            이미지는 png, jpg, jpeg 확장자만
            <br />
            등록 가능합니다. 파일을
            <br />
            재선택 하시겠어요?
          </span>
        ),
        leftButtonLabel: '닫기',
        rightButtonLabel: '재선택',
        onLeftButtonClick: () => {
          setIsShowModal(false);
        },
        onRightButtonClick: () => {
          handleClickReSelect();
        },
      });
      e.target.value = '';
      return;
    }

    if (selectedFiles && selectedFiles.length > 0) {
      const maxFiles = 15;
      const filesArray = Array.from(selectedFiles);

      if (filesArray.length > maxFiles) {
        setPendingFiles(filesArray.slice(0, maxFiles));
        setIsShowModal(true);
        setModalConfig({
          title: '',
          content: (
            <span>
              이미지는 최대 15개까지
              <br />
              전송 가능합니다. 초과된 파일을
              <br />
              제외하고 전송할까요?
            </span>
          ),
          leftButtonLabel: '재선택',
          onLeftButtonClick: () => handleClickReSelect(),
          rightButtonLabel: '전송하기',
          onRightButtonClick: () => handleClickSend(),
        });
      } else {
        updateImageList(filesArray);
      }
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (imageList.length > 0) {
      sendChat();
    }
  }, [imageList]);

  useEffect(() => {
    if (inputRef.current) {
      const observer = new ResizeObserver(() => {
        if (inputRef.current) {
          handleInputHeightChange((inputRef.current as HTMLDivElement).clientHeight);
        }
      });
      observer.observe(inputRef.current, {});

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    if (isTheunban) {
      if (window.FlutterChannel) {
        window.receiveImageFileDataFromFlutter = (data: string) => {
          try {
            const imageData = JSON.parse(data);
            const fileList = imageData
              .map((item: { name: string; data: string }) => {
                try {
                  const byteArray = base64ToBytes(item.data);
                  if (!byteArray) {
                    const error = new Error('Error creating byte array from image data');
                    Sentry.captureException(error);
                    return null;
                  }
                  return new File([byteArray], item.name, { type: 'image/jpeg' });
                } catch (error) {
                  Sentry.captureException(error);
                }
              })
              .filter((file: any): file is File => file !== null);
            updateImageList(fileList);
          } catch (error) {
            Sentry.captureException(error);
          }
        };
      } else {
        const error = new Error('FlutterChannel is not available');
        Sentry.captureException(error);
      }
    }
  }, [isTheunban]);

  const handleClickSend = () => {
    if (pendingFiles) {
      updateImageList(pendingFiles);
    }
    setIsShowModal(false);
  };

  const handleClickReSelect = () => {
    setIsShowModal(false);
    handleClickPlusButton();
  };

  return (
    <>
      <div
        ref={inputRef}
        className="shrink-0 z-30 fixed bottom-0 w-full py-2 px-4 bg-white flex items-center justify-between shadow-[0_-2px_4px_rgba(0,0,0,0.05)] mx-auto left-0 right-0 my-0 max-w-[720px] min-w-[280px] max-h-[700px]"
      >
        <div className="flex rounded-lg mr-[10px] justify-center items-center text-center w-[28px] h-[28px]">
          <div
            className="flex justify-center items-center cursor-pointer border-[1.3px] border-gray-5 rounded-[7px]"
            onClick={handleClickPlusButton}
          >
            <PlusSmallIcon color={colors.gray[5]} width={28} height={28}></PlusSmallIcon>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept="image/*"
            multiple
          />
        </div>

        <ChatInputComponent
          name="message"
          placeholder="메시지 보내기"
          value={chatInput}
          fontSize={16}
          minRows={1}
          maxRows={4}
          onChange={onChangeChat}
        ></ChatInputComponent>
        <div className={`ml-[10px] ${chatInput.trim() ? 'cursor-pointer' : ''}`} onClick={handleSendButtonClick}>
          <PaperAirplaneIcon color={chatInput.trim() ? colors.primary[10] : colors.gray[5]}></PaperAirplaneIcon>
        </div>
      </div>

      <ConfirmModal
        isOpen={isShowModal}
        content={modalConfig.content}
        leftButtonLabel={modalConfig.leftButtonLabel}
        onLeftButtonClick={modalConfig.onLeftButtonClick}
        rightButtonLabel={modalConfig.rightButtonLabel}
        onRightButtonClick={modalConfig.onRightButtonClick}
      />
    </>
  );
};

export default ChatRoomInput;
